import { zodResolver } from '@hookform/resolvers/zod'
import { NormalizedNumber } from '@sparkdotfi/common-universal'
import { useEffect, useState } from 'react'
import { UseFormReturn, useForm } from 'react-hook-form'
import { useChainId } from 'wagmi'

import { TxOverview } from '../types'
import { createTxOverview } from './createTxOverview'
import { validationIssueToMessage } from './validation'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { Objective } from '@/features/actions/logic/types'
import { useActions } from '@/features/actions/logic/useActions'
import { ActionsViewState } from '@/features/actions/types'
import { AssetInputSchema } from '@/features/dialogs/common/logic/form'
import {
  getFieldsForTransferFromUserForm,
  useDebouncedFormValues,
} from '@/features/dialogs/common/logic/transfer-from-user/form'
import { getTransferFromUserFormValidator } from '@/features/dialogs/common/logic/transfer-from-user/validation'
import { FormFieldsForDialog, PageState, PageStatus } from '@/features/dialogs/common/types'
import { useSavingsTokenRepository } from '@/features/savings/common/useSavingsTokenRepository'
import { useSavingsAccountRepository } from '@/features/savings/logic/useSavingsAccountRepository'

export interface UseUpgradeSusdcDialogResult {
  susdc: Token
  susdcBalance: NormalizedNumber
  formFields: FormFieldsForDialog
  form: UseFormReturn<AssetInputSchema>
  pageStatus: PageStatus
  upgradeAmount: NormalizedNumber
  actionsViewState: ActionsViewState
  txOverview: TxOverview
}

export function useUpgradeSusdcDialog(): UseUpgradeSusdcDialogResult {
  const chainId = useChainId()
  const [pageStatus, setPageStatus] = useState<PageState>('form')
  const { account } = useConnectedAddress()

  const tokenRepository = useSavingsTokenRepository({ chainId, account })
  const savingsAccounts = useSavingsAccountRepository({ chainId })
  const susdc = tokenRepository.findOneBySymbol(TokenSymbol('sUSDC'))

  const susdcBalance = tokenRepository.findOneBalance(susdc.symbol)

  const form = useForm<AssetInputSchema>({
    resolver: zodResolver(
      getTransferFromUserFormValidator({
        tokenRepository,
        issueToMessage: validationIssueToMessage,
      }),
    ),
    defaultValues: {
      symbol: susdc.symbol,
      value: susdcBalance.toFixed(),
      isMaxSelected: true,
    },
    mode: 'onChange',
  })

  const {
    debouncedFormValues: formValues,
    isDebouncing,
    isFormValid,
  } = useDebouncedFormValues({
    form,
    tokenRepository,
  })

  const objectives: Objective[] = [
    {
      type: 'upgradeSusdc',
      susdc,
      amount: formValues.value,
    },
  ]
  const actionsEnabled = formValues.value.gt(0) && isFormValid && !isDebouncing

  const { viewState: actionsViewState, reset: resetActions } = useActions({
    objectives,
    context: {
      tokenRepository,
      savingsAccounts,
      chainId,
    },
    onFinish: () => setPageStatus('success'),
    enabled: actionsEnabled,
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: we want to reset actions when the form values change
  useEffect(() => {
    resetActions()
  }, [formValues.token.symbol, formValues.value.toString(), formValues.isMaxSelected, resetActions])

  const txOverview = createTxOverview({
    formValues,
    chainId,
    savingsAccounts,
    susdc,
  })

  return {
    susdc,
    susdcBalance,
    formFields: getFieldsForTransferFromUserForm({ form, tokenRepository }),
    form,
    upgradeAmount: formValues.value,
    actionsViewState,
    pageStatus: {
      actionsEnabled,
      state: pageStatus,
      goToSuccessScreen: () => setPageStatus('success'),
    },
    txOverview,
  }
}
